import { IDocument } from "models/documents/document"

export type DocumentRequestType = "html" | "pdf" | "docx"

export type DocumentType =
  | "None"
  | "QuoteBundle"
  | "PolicyBundle"
  | "Bordereau"
  | "PolicyCancellation"
  | "Static"
  | "UserUploaded"

export function getDocumentViewUrl(
  documentUrl: string | null | undefined,
  documentType?: DocumentType | null
): string | null {
  if (!documentUrl || typeof documentUrl !== "string") return null
  if (documentType === "Static") return documentUrl
  return `/document?url=${encodeURIComponent(documentUrl)}`
}

export function getDocumentDownloadUrl(
  documentUrl: string | null | undefined,
  requestType: DocumentRequestType
): string | null {
  if (!documentUrl || typeof documentUrl !== "string") return null
  const separator = documentUrl.includes("?") ? "&" : "?"
  return `${documentUrl}${separator}requesttype=${requestType}`
}

const isPolicyDocument = (document: IDocument) =>
  document.type === "PolicyDocument"

// Deduplicates non-policy documents (e.g. static PDS/FSG) by reference ID, keeping only
// the latest version. Policy documents retain every version.
export function deduplicatePolicyDocuments(
  documents: IDocument[] | undefined
): IDocument[] {
  if (!documents?.length) return []

  const latestByReferenceId = new Map<string, IDocument>()

  for (const document of documents) {
    if (isPolicyDocument(document)) continue

    const key = document.referenceID || document.id
    const existing = latestByReferenceId.get(key)

    if (!existing || document.version > existing.version) {
      latestByReferenceId.set(key, document)
    }
  }

  const seenReferenceIds = new Set<string>()
  const deduped: IDocument[] = []

  for (const document of documents) {
    if (isPolicyDocument(document)) {
      deduped.push(document)
      continue
    }

    const key = document.referenceID || document.id

    if (seenReferenceIds.has(key)) continue

    seenReferenceIds.add(key)
    deduped.push(latestByReferenceId.get(key) ?? document)
  }

  const seenIds = new Set<string>()

  return deduped.filter((document) => {
    if (!document.id) return true
    if (seenIds.has(document.id)) return false
    seenIds.add(document.id)
    return true
  })
}

export function getPolicyDocumentRowKey(
  document: IDocument,
  index?: number
): string {
  if (document.id) {
    return `${document.id}-${document.referenceID}-${document.version}`
  }

  return `${document.referenceID}-${document.version}-${index ?? 0}`
}
